import type { ReactNode } from "react"
import { useState } from "react"
import { motion } from "motion/react"
import {
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
	IconCopyOutline18 as Copy,
} from "nucleo-ui-outline-18"

import type { AiChatMessage } from "@productlane/lib/src/ai"
import { normalizeAiChatHtml } from "@productlane/lib/src/ai"
import { cn } from "@productlane/lib/src/cn"

import { useCopiedFlag } from "../../hooks/useCopiedFlag"
import { useTextClamped } from "../../hooks/useTextClamped"
import { IconButton } from "../icon-button"
import { SwapText } from "../swap-text"
import { AiChatMessageBody } from "./message-body"
import { ScrollToLatest } from "./scroll-to-latest"
import { AiThinkingOrb, pickOrbState } from "./thinking-orb"
import { useStickToBottom } from "./use-stick-to-bottom"

export const AiChatBubbleUser = ({
	message,
	className,
	disableEntranceAnimation = false,
	contentSlot,
	bodyClassName,
}: {
	imageUrl?: string
	message: AiChatMessage
	className?: string
	// The bubble itself, so a full-page chat can size it apart from the row.
	bodyClassName?: string
	disableEntranceAnimation?: boolean
	// Renders the prompt with its own markup, so a surface that pinned records
	// can show them as badges. Copy still takes the plain text.
	contentSlot?: ReactNode
}) => {
	const [expanded, setExpanded] = useState(false)
	const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null)
	const isClamped = useTextClamped(bodyEl, !expanded, message.content)
	const [copied, markCopied] = useCopiedFlag(2000)

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(message.content)
			markCopied()
		} catch {
			console.error("Failed to copy to clipboard")
		}
	}

	return (
		<motion.div
			initial={disableEntranceAnimation ? false : { opacity: 0, x: 10 }}
			animate={{ opacity: 1, x: 0 }}
			transition={{ duration: 0.15 }}
			className={cn(
				"group/user-message ml-auto flex w-fit min-w-0 max-w-[75%] flex-col items-end",
				className,
			)}
		>
			<div className="relative w-full min-w-0">
				<div
					ref={setBodyEl}
					data-testid="ai-chat-user-message"
					className={cn(
						// A pasted token with no spaces sets the flex item's min-content
						// width, so without min-w-0 and anywhere-wrapping the bubble grows
						// past the panel instead of wrapping. Newlines survive too, which
						// is what a pasted log needs.
						"prose w-full min-w-0 cursor-text select-text whitespace-pre-wrap rounded-md bg-gray-150 px-2.5 py-1.5 text-left !text-[0.94rem] leading-normal [overflow-wrap:anywhere] dark:prose-invert dark:border-gray-300 dark:bg-gray-750 dark:text-gray-100",
						!expanded && "line-clamp-[10]",
						isClamped && "pb-6",
						className,
						bodyClassName,
					)}
				>
					{contentSlot ?? message.content}
				</div>
				{isClamped && (
					<>
						{!expanded && (
							<div
								aria-hidden
								className="pointer-events-none absolute inset-x-0 bottom-0 h-10 rounded-b-md bg-gradient-to-t from-gray-150 via-gray-150/80 to-transparent dark:from-gray-750 dark:via-gray-750/80"
							/>
						)}
						<IconButton
							Icon={ChevronDown}
							iconStrokeWidth={1.5}
							variant="ghost"
							size="sm"
							aria-label={expanded ? "Show less" : "Show more"}
							onClick={() => setExpanded((v) => !v)}
							className="absolute bottom-1 left-1/2 h-4 w-4 -translate-x-1/2 text-gray-400 hover:text-primary dark:text-gray-500"
							iconClassName={cn("h-3 w-3", expanded && "rotate-180")}
						/>
					</>
				)}
			</div>
			<div className="flex h-4 items-center">
				<IconButton
					Icon={copied ? Check : Copy}
					iconStrokeWidth={copied ? 2 : 1.75}
					variant="ghost"
					size="sm"
					aria-label="Copy prompt"
					onClick={handleCopy}
					className="h-4 w-4 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/user-message:opacity-100"
					iconClassName="h-3 w-3"
				/>
			</div>
		</motion.div>
	)
}

export const AiChatBubbleAssistant = ({
	message,
	isStreaming = false,
	thinkingLabel = "Thinking",
	header,
	inlineSuffix,
	enrichments,
	editorClasses = "",
	disableEntranceAnimation = false,
}: {
	message: AiChatMessage
	isStreaming?: boolean
	thinkingLabel?: string
	/** Status of work behind this turn. Sits above the answer. */
	header?: ReactNode
	/** Flows with the answer's last line, so it needs inline children. */
	inlineSuffix?: ReactNode
	enrichments?: ReactNode
	editorClasses?: string
	disableEntranceAnimation?: boolean
}) => {
	const htmlContent = message.content
		? normalizeAiChatHtml(message.content)
		: ""
	const hasImages = Object.keys(message.images ?? {}).length > 0

	return (
		<motion.div
			initial={disableEntranceAnimation ? false : { opacity: 0, y: 10 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.15 }}
			className="group/message flex min-w-0 flex-col px-1"
			data-streaming={isStreaming ? "true" : undefined}
		>
			{header}
			{(htmlContent || hasImages) && (
				<div
					className={cn(
						"ai-chat-message prose min-w-0 select-text !text-[0.95rem] tracking-tight text-primary [overflow-wrap:anywhere] dark:prose-invert [&_a]:[overflow-wrap:anywhere] [&_pre]:overflow-x-auto",
						// The body renders one block per segment, so the answer's last
						// paragraph and the suffix only share a line once both go
						// inline. A list or code block at the end stays put and the
						// suffix lands under it.
						inlineSuffix
							? "[&>div:last-of-type>p:last-child]:inline [&>div:last-of-type]:inline"
							: undefined,
						editorClasses,
					)}
				>
					<AiChatMessageBody
						html={htmlContent}
						images={message.images}
						isStreaming={isStreaming}
					/>
					{inlineSuffix}
				</div>
			)}
			{!message.processed && (
				<div
					className={cn(
						"mb-6 flex items-center gap-2 text-[0.85rem] text-secondary",
						(htmlContent || hasImages) && "mt-2.5",
					)}
				>
					<AiThinkingOrb
						state={pickOrbState(message.waitAction)}
						size={20}
						className="shrink-0"
						ariaLabel={message.waitAction ?? thinkingLabel}
					/>
					<SwapText
						value={`${message.waitAction ? message.waitAction : thinkingLabel} ...`}
						textClassName="ai-loading-shimmer"
					/>
				</div>
			)}

			{enrichments}
		</motion.div>
	)
}

export const AiChatAutoScrollContainer = ({
	children,
	className,
	lastMessage,
	scrollClassName,
	startAtTop,
}: {
	children: ReactNode
	className?: string
	lastMessage?: AiChatMessage | null
	// Full-page chats reserve the scrollbar gutter so the column never shifts.
	scrollClassName?: string
	startAtTop?: boolean
}) => {
	const { containerRef, contentRef, isAwayFromBottom, scrollToLatest } =
		useStickToBottom({
			followKey: lastMessage?.role === "user" ? lastMessage.id : null,
			startAtTop,
		})

	return (
		<div className="relative h-full">
			<div
				className={cn("h-full overflow-y-auto", scrollClassName)}
				ref={containerRef}
			>
				<div className={className} ref={contentRef}>
					{children}
				</div>
			</div>
			<ScrollToLatest
				isVisible={isAwayFromBottom}
				onClick={scrollToLatest}
				label="Jump to the latest message"
			/>
		</div>
	)
}
